/**
 * Admin idle re-verify overlay (Task #6455).
 *
 * Shown when the admin idle watcher fires after 2 hours of inactivity.
 * Covers the entire admin UI with a blocking overlay so no admin data is
 * interactable until the admin re-proves identity. On success the overlay
 * clears and the admin resumes exactly where they left off (no navigation,
 * no lost state). On failure or explicit sign-out the session is terminated.
 *
 * Why a separate component (not reusing StepUpAuthDialog):
 *   StepUpAuthDialog is a Radix Dialog — it can be dismissed by pressing
 *   Escape or clicking outside, which would resolve/reject via the event
 *   system. For the idle case we need a non-dismissable overlay; the only
 *   escape is successful re-verification or an explicit sign-out.
 *
 * The verify flow mirrors StepUpAuthDialog exactly (password / email-OTP /
 * TOTP authenticator), but POSTs to /api/auth/step-up directly rather than
 * going through the window-event dispatch.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldAlert } from "lucide-react";
import { useUser } from "@/hooks/useUser";
import { getSupabaseClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@workspace/ui/components/ui/input";

interface Props {
  onSuccess: () => void;
  onSignOut: () => void;
}

type Mode = "password" | "otp" | "mfa";

export function AdminIdleReVerifyOverlay({ onSuccess, onSignOut }: Props) {
  const { session } = useUser();
  const [mode, setMode] = useState<Mode>("password");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [totpFactorId, setTotpFactorId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const supabase = getSupabaseClient();
    if (!supabase) return;
    supabase.auth.mfa
      .listFactors()
      .then(({ data }) => {
        if (cancelled) return;
        const factor = data?.totp?.[0];
        setTotpFactorId(factor?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setTotpFactorId(null);
      });
    return () => { cancelled = true; };
  }, []);

  const postStepUp = useCallback(
    async (body: Record<string, unknown>): Promise<Response> => {
      const token = session?.access_token;
      if (!token) throw new Error("session_expired");
      return fetch(`${import.meta.env.BASE_URL || "/"}api/auth/step-up`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
    },
    [session?.access_token],
  );

  const handleSignOut = useCallback(() => {
    setSubmitting(true);
    onSignOut();
  }, [onSignOut]);

  const submit = useCallback(async () => {
    setError(null);
    setSubmitting(true);
    try {
      if (mode === "password") {
        let res: Response;
        try {
          res = await postStepUp({ method: "password", password });
        } catch {
          onSignOut();
          return;
        }
        if (res.status === 401 || res.status === 403) {
          onSignOut();
          return;
        }
        if (res.status === 409) {
          setMode("otp");
          setError("This account has no password. We'll email you a code instead.");
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const code = typeof body?.error === "string" ? body.error : "";
          if (res.status === 429) { setError("Too many attempts. Please wait and try again."); return; }
          if (res.status === 503) { setError("Verification temporarily unavailable. Please try again."); return; }
          setError(code === "Incorrect password" ? "Incorrect password. Try again." : code || `Verification failed (${res.status})`);
          return;
        }
      } else if (mode === "mfa") {
        if (!totpFactorId) { setError("No authenticator is set up on this account."); return; }
        const supabase = getSupabaseClient();
        if (!supabase) { setError("Authenticator check unavailable. Try another method."); return; }
        const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: totpFactorId });
        if (challengeError || !challenge) { setError("Couldn't start the authenticator check. Try again."); return; }
        let res: Response;
        try {
          res = await postStepUp({ method: "mfa", factorId: totpFactorId, challengeId: challenge.id, code: mfaCode });
        } catch {
          onSignOut();
          return;
        }
        if (res.status === 401 || res.status === 403) { onSignOut(); return; }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const code = typeof body?.error === "string" ? body.error : "";
          if (res.status === 429) { setError("Too many attempts. Please wait and try again."); return; }
          if (res.status === 503) { setError("Verification temporarily unavailable. Please try again."); return; }
          setError(code || "Invalid authenticator code.");
          return;
        }
      } else {
        if (!otpRequested) {
          let res: Response;
          try {
            res = await postStepUp({ method: "otp_request" });
          } catch {
            onSignOut();
            return;
          }
          if (res.status === 401 || res.status === 403) { onSignOut(); return; }
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            const code = typeof body?.error === "string" ? body.error : "";
            if (res.status === 429) { setError("Too many attempts. Please wait and try again."); return; }
            if (res.status === 503) { setError("Verification temporarily unavailable. Please try again."); return; }
            if (code === "no_email_on_file") { setError("No email address is associated with this account."); return; }
            setError("Couldn't send the code. Please try again.");
            return;
          }
          setOtpRequested(true);
          setError("Code sent. Check your email.");
          return;
        }
        let res: Response;
        try {
          res = await postStepUp({ method: "otp", code: otp });
        } catch {
          onSignOut();
          return;
        }
        if (res.status === 401 || res.status === 403) { onSignOut(); return; }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const code = typeof body?.error === "string" ? body.error : "";
          if (res.status === 429) { setError("Too many attempts. Please wait and try again."); return; }
          if (res.status === 503) { setError("Verification temporarily unavailable. Please try again."); return; }
          setError(code || "Invalid or expired code.");
          return;
        }
      }
      onSuccess();
    } catch {
      onSignOut();
    } finally {
      setSubmitting(false);
    }
  }, [mode, password, otp, otpRequested, mfaCode, totpFactorId, postStepUp, onSuccess, onSignOut]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Session verification required"
      className="fixed inset-0 z-[9999] flex items-center justify-center material-scrim"
    >
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold leading-tight">
              Session idle. Please verify it&apos;s you.
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Your admin session has been idle for 2 hours. Re-verify to continue where you left off.
            </p>
          </div>
        </div>

        <div className="mt-4 flex gap-1.5">
          <Button
            type="button"
            size="sm"
            variant={mode === "password" ? "default" : "outline"}
            onClick={() => { setMode("password"); setError(null); }}
            disabled={submitting}
            aria-pressed={mode === "password"}
            className="flex-1"
          >
            Password
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === "otp" ? "default" : "outline"}
            onClick={() => { setMode("otp"); setOtpRequested(false); setError(null); }}
            disabled={submitting}
            aria-pressed={mode === "otp"}
            className="flex-1"
          >
            Email code
          </Button>
          {totpFactorId && (
            <Button
              type="button"
              size="sm"
              variant={mode === "mfa" ? "default" : "outline"}
              onClick={() => { setMode("mfa"); setError(null); }}
              disabled={submitting}
              aria-pressed={mode === "mfa"}
              className="flex-1"
            >
              Authenticator
            </Button>
          )}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); void submit(); }}
          className="mt-3 space-y-3"
        >
          {mode === "password" ? (
            <div className="space-y-1.5">
              <label htmlFor="idle-reverify-password" className="text-sm font-medium">
                Password
              </label>
              <Input
                id="idle-reverify-password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
              />
            </div>
          ) : mode === "mfa" ? (
            <div className="space-y-1.5">
              <label htmlFor="idle-reverify-mfa" className="text-sm font-medium">
                6-digit authenticator code
              </label>
              <Input
                id="idle-reverify-mfa"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                disabled={submitting}
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              <label htmlFor="idle-reverify-otp" className="text-sm font-medium">
                {otpRequested ? "Enter the code from your email" : "We'll send a code to your email"}
              </label>
              {otpRequested && (
                <Input
                  id="idle-reverify-otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  disabled={submitting}
                />
              )}
            </div>
          )}

          {error && (
            <p role="alert" className="text-sm text-destructive">{error}</p>
          )}

          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={handleSignOut}
              disabled={submitting}
              className="cursor-pointer text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
            >
              Sign out
            </button>
            <Button
              type="submit"
              disabled={
                submitting
                || (mode === "password" && !password)
                || (mode === "otp" && otpRequested && !otp)
                || (mode === "mfa" && !mfaCode)
              }
            >
              {submitting
                ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Verifying…</>
                : mode === "otp" && !otpRequested
                  ? "Send code"
                  : "Continue"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
